import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Layers, Check, Plus, X, Globe, Trash } from "lucide-react";

interface Scope {
  code: string;
  name: string;
}

interface WebhookEntry {
  object_type: string;
  url: string;
}

// Type for the webhook data format used in the database
// Each entry is an object with a single key-value pair where
// the key is the object_type and the value is the URL
type WebhookData = Record<string, string>[];

// Interface for the integration object
interface Integration {
  id: string;
  hotel_id: string;
  scopes: string[];
  status: string;
  webhooks?: WebhookData | null;
}

export function FunctionalScope() {
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Trigger webhook related states
  const [showTriggerSection, setShowTriggerSection] = useState(false);
  const [webhooks, setWebhooks] = useState<WebhookEntry[]>([]);
  const [availableObjectTypes, setAvailableObjectTypes] = useState<Scope[]>([]);
  const [newWebhook, setNewWebhook] = useState<WebhookEntry>({
    object_type: "",
    url: "",
  });
  const [isAddingWebhook, setIsAddingWebhook] = useState(false);
  const [editingWebhookIndex, setEditingWebhookIndex] = useState<number | null>(
    null
  );
  const [editingWebhookUrl, setEditingWebhookUrl] = useState("");

  useEffect(() => {
    loadScopes();
  }, []);

  useEffect(() => {
    // Check if "triggers" is selected to show the webhook section
    const hasTriggerScope = scopes
      .filter((scope) => selectedScopes.includes(scope.code))
      .some((scope) => scope.code.toLowerCase() === "triggers");

    setShowTriggerSection(hasTriggerScope);

    // Get selected scopes to use as object types for webhooks
    if (hasTriggerScope) {
      // Get all selected scopes except the "triggers" scope itself
      const selectedScopeObjects = scopes.filter(
        (scope) =>
          selectedScopes.includes(scope.code) &&
          scope.code.toLowerCase() !== "triggers"
      );

      // Filter out scopes that are already used in webhooks
      const usedTypes = webhooks.map((webhook) => webhook.object_type);
      const availableScopeObjects = selectedScopeObjects.filter(
        (scope) => !usedTypes.includes(scope.code)
      );

      setAvailableObjectTypes(availableScopeObjects);
    } else {
      // If triggers scope is not selected, clear available object types
      setAvailableObjectTypes([]);
    }
  }, [selectedScopes, scopes, webhooks]);

  const loadScopes = async () => {
    try {
      setLoading(true);

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      // Load all available scopes
      const { data: scopesData, error: scopesError } = await supabase
        .from("pms_scopes")
        .select("code, name")
        .order("name");

      if (scopesError) throw scopesError;

      // Load user's selected scopes and webhooks from saas_details
      const { data: saasDetails, error: saasError } = await supabase
        .from("saas_details")
        .select("scopes, webhooks")
        .eq("id", user.id)
        .single();

      if (saasError) throw saasError;

      setScopes(scopesData || []);

      // Set selected scopes from saved scope codes
      if (saasDetails?.scopes) {
        setSelectedScopes(saasDetails.scopes);
      }

      // Load webhooks if they exist
      if (
        saasDetails?.webhooks &&
        Array.isArray(saasDetails.webhooks) &&
        saasDetails.webhooks.length > 0
      ) {
        const webhookEntries: WebhookEntry[] = [];

        // Convert from [{object_type: url}] format to WebhookEntry[] format
        saasDetails.webhooks.forEach((entry: Record<string, string>) => {
          // Each entry is an object with a single key-value pair
          const objectType = Object.keys(entry)[0];
          const url = entry[objectType];

          webhookEntries.push({
            object_type: objectType,
            url: url,
          });
        });

        setWebhooks(webhookEntries);
      }

      // The availableObjectTypes will be set in the useEffect based on selected scopes
    } catch (err) {
      console.error("Error loading scopes:", err);
      setError("Failed to load functional scopes");
    } finally {
      setLoading(false);
    }
  };

  const handleScopeToggle = (scopeCode: string) => {
    // If we're deselecting a scope
    if (selectedScopes.includes(scopeCode)) {
      // If it's the "triggers" scope, clear all webhooks
      if (scopeCode.toLowerCase() === "triggers") {
        setWebhooks([]);
      }
      // If it's another scope, remove any webhooks that use this scope
      else {
        setWebhooks((prevWebhooks) =>
          prevWebhooks.filter((webhook) => webhook.object_type !== scopeCode)
        );
      }

      // Remove the scope from selected scopes
      setSelectedScopes((prev) => prev.filter((code) => code !== scopeCode));
    }
    // If we're selecting a scope, just add it
    else {
      setSelectedScopes((prev) => [...prev, scopeCode]);
    }
  };

  const handleAddWebhook = () => {
    setIsAddingWebhook(true);
    setNewWebhook({
      object_type:
        availableObjectTypes.length > 0 ? availableObjectTypes[0].code : "",
      url: "",
    });
  };

  const handleCancelAddWebhook = () => {
    setIsAddingWebhook(false);
    setNewWebhook({ object_type: "", url: "" });
  };

  const handleSaveWebhook = () => {
    // Validate webhook
    if (!newWebhook.object_type || !newWebhook.url) {
      setError("Please select a scope and enter a webhook URL");
      return;
    }

    // Add new webhook
    setWebhooks([...webhooks, newWebhook]);

    // The useEffect will automatically update availableObjectTypes
    // when webhooks change, so we don't need to manually update it here

    // Reset form
    setIsAddingWebhook(false);
    setNewWebhook({ object_type: "", url: "" });
    setError("");
  };

  const handleRemoveWebhook = (index: number) => {
    // The useEffect will automatically update availableObjectTypes
    // when webhooks change, so we don't need to manually update it here

    // Remove the webhook
    setWebhooks(webhooks.filter((_, i) => i !== index));

    // If we were editing this webhook, cancel editing
    if (editingWebhookIndex === index) {
      setEditingWebhookIndex(null);
      setEditingWebhookUrl("");
    }
  };

  const handleEditWebhook = (index: number) => {
    setEditingWebhookIndex(index);
    setEditingWebhookUrl(webhooks[index].url);
  };

  const handleCancelEditWebhook = () => {
    setEditingWebhookIndex(null);
    setEditingWebhookUrl("");
  };

  const handleSaveEditWebhook = () => {
    if (editingWebhookIndex === null) return;

    // Validate URL
    if (!editingWebhookUrl.trim()) {
      setError("Please enter a valid webhook URL");
      return;
    }

    // Update the webhook URL
    const updatedWebhooks = [...webhooks];
    updatedWebhooks[editingWebhookIndex] = {
      ...updatedWebhooks[editingWebhookIndex],
      url: editingWebhookUrl,
    };

    setWebhooks(updatedWebhooks);
    setEditingWebhookIndex(null);
    setEditingWebhookUrl("");
    setError("");
  };

  const handleSave = async () => {
    console.log("=== SAVE STARTED ===");
    setSaving(true);
    setError("");
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      // We're already using scope codes in selectedScopes
      // No need to map or transform

      // Check if triggers scope is selected
      const hasTriggerScope = selectedScopes.some(
        (code) => code.toLowerCase() === "triggers"
      );

      // Prepare webhooks data only if triggers scope is selected
      let webhooksData: WebhookData | null = null;
      if (hasTriggerScope) {
        // Convert webhooks to the desired format: [{object_type: url}]
        webhooksData = webhooks.map((webhook) => {
          const entry: Record<string, string> = {};
          entry[webhook.object_type] = webhook.url;
          return entry;
        });

        // If no webhooks are configured but triggers is selected, use empty array
        if (webhooksData.length === 0) {
          webhooksData = [];
        }
      }
      // Update saas_details with selected scope codes and webhooks
      const { error: updateError } = await supabase
        .from("saas_details")
        .update({
          scopes: selectedScopes,
          webhooks: webhooksData, // Will be null if triggers is not selected
        })
        .eq("id", user.id);

      if (updateError) throw updateError;

      // Now update the integrations table for all connected hotels
      // First, get all integrations for this SaaS, regardless of status
      const { data: integrations, error: integrationsError } = await supabase
        .from("integrations")
        .select("id, hotel_id, scopes, status, webhooks")
        .eq("saas_id", user.id);

      if (integrationsError) throw integrationsError;
      // For each integration, get the connector scopes and calculate the intersection
      if (integrations && integrations.length > 0) {
        // Get all hotel IDs to fetch their connector scopes
        const hotelIds = integrations.map(
          (integration) => integration.hotel_id
        );

        // Get connector codes for each hotel
        const { data: credentials, error: credentialsError } = await supabase
          .from("credentials")
          .select("hotel_id, connector_code")
          .in("hotel_id", hotelIds)
          .eq("connector_type", "pms")
          .eq("is_primary", true);

        if (credentialsError) throw credentialsError;
        // Create a map of hotel ID to connector code
        const hotelToConnectorMap = new Map();
        if (credentials) {
          credentials.forEach((cred) => {
            hotelToConnectorMap.set(cred.hotel_id, cred.connector_code);
          });
        }

        // Get connector scopes for each connector code
        const connectorCodes = Array.from(
          new Set(
            credentials?.map((cred) => cred.connector_code.toLowerCase()) || []
          )
        );

        const { data: connectors, error: connectorsError } = await supabase
          .from("connectors")
          .select("code, scopes, trigger_objects")
          .in("code", connectorCodes);
        if (connectorsError) throw connectorsError;

        // Create maps of connector code to scopes and trigger_objects
        const connectorToScopesMap = new Map();
        const connectorToTriggerObjectsMap = new Map();
        if (connectors) {
          connectors.forEach((connector) => {
            connectorToScopesMap.set(
              connector.code.toLowerCase(),
              connector.scopes || []
            );
            connectorToTriggerObjectsMap.set(
              connector.code.toLowerCase(),
              connector.trigger_objects || []
            );
          });
        }

        // Update each integration with the new intersection of scopes
        for (const integration of integrations as Integration[]) {
          const connectorCode = hotelToConnectorMap.get(integration.hotel_id);

          if (connectorCode) {
            const connectorScopes =
              connectorToScopesMap.get(connectorCode.toLowerCase()) || [];
            // Calculate intersection of SaaS scopes and connector scopes
            const intersectionScopes = selectedScopes.filter((scope) =>
              connectorScopes.includes(scope)
            );

            // Check if triggers is in the intersection
            const hasTriggers = intersectionScopes.some(
              (scope) => scope.toLowerCase() === "triggers"
            );

            let filteredWebhooks: WebhookData | null = null;

            if (hasTriggers && webhooksData) {
              // Get the connector's trigger_objects
              let rawTriggerObjects =
                connectorToTriggerObjectsMap.get(connectorCode.toLowerCase()) ||
                [];

              // Handle different possible formats of trigger_objects
              let connectorTriggerObjects: string[] = [];

              if (Array.isArray(rawTriggerObjects)) {
                // If it's an array, convert each item to a string
                connectorTriggerObjects = rawTriggerObjects.map((item) => {
                  if (typeof item === "string") {
                    return item;
                  } else if (typeof item === "object" && item !== null) {
                    // If it's an object, try to get the first key
                    const keys = Object.keys(item);
                    if (keys.length > 0) {
                      return keys[0];
                    }
                  }
                  // Default fallback
                  return String(item);
                });
              } else if (
                typeof rawTriggerObjects === "object" &&
                rawTriggerObjects !== null
              ) {
                // If it's a single object, extract its keys
                connectorTriggerObjects = Object.keys(rawTriggerObjects);
              } else {
                // Fallback for any other case
                connectorTriggerObjects = [String(rawTriggerObjects)];
              }
              // Extract webhook object types
              const webhookObjectTypes = webhooksData.map(
                (entry) => Object.keys(entry)[0]
              );

              console.log(`Webhook object types:`, webhookObjectTypes);

              // Find common object types (case-insensitive)
              const commonObjectTypes = new Set<string>();

              for (const webhookType of webhookObjectTypes) {
                for (const triggerType of connectorTriggerObjects) {
                  if (webhookType.toLowerCase() === triggerType.toLowerCase()) {
                    commonObjectTypes.add(webhookType);
                    break;
                  }
                }
              }
              // Filter webhooks to only include entries for common object types
              filteredWebhooks = webhooksData.filter((webhookEntry) => {
                const objectType = Object.keys(webhookEntry)[0];
                const isCommon = commonObjectTypes.has(objectType);
                return isCommon;
              });

              // If no common objects, don't include triggers in the scopes
              if (filteredWebhooks.length === 0) {
                // Remove triggers from intersection scopes
                const triggerIndex = intersectionScopes.findIndex(
                  (scope) => scope.toLowerCase() === "triggers"
                );
                if (triggerIndex !== -1) {
                  intersectionScopes.splice(triggerIndex, 1);
                }
                // Set filteredWebhooks to null
                // This matches the behavior in PMSTable.tsx
                filteredWebhooks = null;
              }
            }

            // Check if webhooks have changed to avoid unnecessary updates
            const currentWebhooks = integration.webhooks || null;
            const webhooksChanged =
              JSON.stringify(filteredWebhooks) !==
              JSON.stringify(currentWebhooks);

            const { data: updateResult, error: updateIntegrationError } =
              await supabase
                .from("integrations")
                .update({
                  scopes: intersectionScopes,
                  // Update webhooks only if triggers is in the intersection and there are common objects
                  webhooks: filteredWebhooks,
                })
                .eq("id", integration.id);

            if (updateIntegrationError) {
              console.error(
                `Error updating integration ${integration.id}:`,
                updateIntegrationError
              );
            } else {
              console.log(`Successfully updated integration ${integration.id}`);
            }

            // Error handling is now inside the if block above
          }
        }
      }

      setIsEditing(false);
      setError("Scopes updated successfully");
      setTimeout(() => setError(""), 3000);
    } catch (err: any) {
      console.error("Error saving scopes:", err);
      setError(err.message || "Failed to update scopes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">
            Loading functional scopes...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Layers className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-600">
              Functional Scope
            </h2>
          </div>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-primary inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Modify Scope
            </button>
          ) : (
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setIsEditing(false);
                  loadScopes();
                }}
                className="btn-secondary inline-flex items-center"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary inline-flex items-center"
              >
                <Check className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div
            className={`mb-4 p-4 rounded-md ${
              error.includes("successfully") ? "bg-green-50" : "bg-red-50"
            }`}
          >
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          {scopes.map((scope) => (
            <div
              key={scope.code}
              className={`
                relative flex items-center p-4 rounded-lg border
                ${isEditing ? "cursor-pointer" : ""}
                ${
                  (isEditing && selectedScopes.includes(scope.code)) ||
                  (!isEditing && selectedScopes.includes(scope.code))
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-gray-200"
                }
              `}
              onClick={() => isEditing && handleScopeToggle(scope.code)}
            >
              <div className="flex items-center justify-between w-full">
                <div className="text-sm font-medium text-gray-900">
                  {scope.name}
                </div>
                {((isEditing && selectedScopes.includes(scope.code)) ||
                  (!isEditing && selectedScopes.includes(scope.code))) && (
                  <Check className="h-5 w-5 text-indigo-500" />
                )}
              </div>
            </div>
          ))}
        </div>

        {scopes.length === 0 && (
          <div className="text-center py-8">
            <p className="px-6 py-4 text-center text-sm text-gray-500">
              No functional scopes available.
            </p>
          </div>
        )}

        {/* Trigger Webhooks Section */}
        {showTriggerSection && (
          <div className="mt-8 border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Globe className="h-5 w-5 text-indigo-600" />
                <h3 className="text-lg font-medium text-gray-700">
                  Trigger Webhooks
                </h3>
              </div>
              {isEditing &&
                !isAddingWebhook &&
                availableObjectTypes.length > 0 && (
                  <button
                    onClick={handleAddWebhook}
                    className="btn-secondary inline-flex items-center text-sm"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Webhook
                  </button>
                )}
            </div>

            {/* Add Webhook Form */}
            {isEditing && isAddingWebhook && (
              <div className="mb-6 p-4 border border-indigo-100 rounded-lg bg-indigo-50">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Add New Webhook
                </h4>

                <div className="grid grid-cols-1 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Object Type
                    </label>
                    <select
                      value={newWebhook.object_type}
                      onChange={(e) =>
                        setNewWebhook({
                          ...newWebhook,
                          object_type: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      {availableObjectTypes.length === 0 ? (
                        <option value="">
                          {selectedScopes.some(
                            (code) => code.toLowerCase() === "triggers"
                          )
                            ? "No available scopes - all are already configured"
                            : "Select at least one scope to configure triggers"}
                        </option>
                      ) : (
                        availableObjectTypes.map((scope: Scope) => (
                          <option key={scope.code} value={scope.code}>
                            {scope.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Webhook URL
                    </label>
                    <input
                      type="url"
                      value={newWebhook.url}
                      onChange={(e) =>
                        setNewWebhook({ ...newWebhook, url: e.target.value })
                      }
                      placeholder="https://example.com/webhook"
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCancelAddWebhook}
                    className="btn-secondary text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveWebhook}
                    className="btn-primary text-sm"
                    disabled={!newWebhook.object_type || !newWebhook.url}
                  >
                    Add Webhook
                  </button>
                </div>
              </div>
            )}

            {/* Webhooks List */}
            {webhooks.length > 0 ? (
              <div className="grid grid-cols-1 gap-3">
                {webhooks.map((webhook, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    {editingWebhookIndex === index ? (
                      <div className="w-full">
                        <div className="font-medium text-sm text-gray-800 mb-2">
                          {scopes.find(
                            (scope) => scope.code === webhook.object_type
                          )?.name || webhook.object_type}
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="url"
                            value={editingWebhookUrl}
                            onChange={(e) =>
                              setEditingWebhookUrl(e.target.value)
                            }
                            className="flex-1 p-2 text-sm border border-gray-300 rounded-md"
                            placeholder="https://example.com/webhook"
                          />
                          <div className="flex space-x-2">
                            <button
                              onClick={handleCancelEditWebhook}
                              className="p-1 text-gray-500 hover:text-gray-700"
                              title="Cancel"
                            >
                              <X className="h-4 w-4" />
                            </button>
                            <button
                              onClick={handleSaveEditWebhook}
                              className="p-1 text-green-500 hover:text-green-700"
                              title="Save"
                              disabled={!editingWebhookUrl.trim()}
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="font-medium text-sm text-gray-800">
                            {scopes.find(
                              (scope) => scope.code === webhook.object_type
                            )?.name || webhook.object_type}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-md">
                            {webhook.url}
                          </div>
                        </div>
                        {isEditing && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditWebhook(index)}
                              className="text-blue-500 hover:text-blue-700"
                              title="Edit"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-4 w-4"
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRemoveWebhook(index)}
                              className="text-red-500 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 border border-dashed rounded-lg">
                <p className="text-sm text-gray-500">
                  No webhooks configured.{" "}
                  {isEditing && availableObjectTypes.length > 0
                    ? 'Click "Add Webhook" to create one.'
                    : ""}
                </p>
              </div>
            )}

            {isEditing &&
              availableObjectTypes.length === 0 &&
              webhooks.length > 0 && (
                <div className="mt-3 text-sm text-gray-500">
                  All selected scopes have been configured. Remove an existing
                  webhook or select more scopes to add more webhooks.
                </div>
              )}

            {isEditing &&
              availableObjectTypes.length === 0 &&
              webhooks.length === 0 &&
              selectedScopes.some(
                (code) => code.toLowerCase() === "triggers"
              ) && (
                <div className="mt-3 text-sm text-gray-500">
                  Select at least one scope besides "Triggers" to configure
                  webhooks.
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
}
